'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  slug: string;
  author: {
    name: string;
    role: string;
  };
  image: string;
  imageAlt: string;
  readTime: number;
  publishedDate: string;
  tags: string[];
  bookmarked: boolean;
}

interface ResourceCardProps {
  resource: Resource;
  onBookmark?: (id: string) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onBookmark,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHydrated) return;
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateXValue = ((y - centerY) / centerY) * -10;
    const rotateYValue = ((x - centerX) / centerX) * 10;

    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleMouseLeave = () => {
    if (!isHydrated) return;
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };

  const cardStyle = isHydrated && isHovered
    ? {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`,
      transition: 'transform 0.1s ease-out',
    }
    : {
      transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
      transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    };

  return (
    <Link href={`/knowledge-hub/${resource.slug}`} className="block h-full">
      <div
        className="bg-surface rounded-2xl overflow-hidden border border-border hover:shadow-xl transition-shadow duration-300 h-full"
        style={cardStyle}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => isHydrated && setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative h-48 overflow-hidden">
          <AppImage
            src={resource.image}
            alt={resource.imageAlt}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
          {onBookmark && (
            <div className="absolute top-4 right-4">
              <button
                type="button"
                aria-label={
                  resource.bookmarked
                    ? `Remove bookmark for ${resource.title}`
                    : `Bookmark ${resource.title}`
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (!isHydrated || !onBookmark) return;
                  onBookmark(resource.id);
                }}
                disabled={!isHydrated}
                className={`p-2 rounded-full backdrop-blur-md transition-all duration-300 ${
                  resource.bookmarked
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface/80 text-text-secondary hover:bg-surface'
                }`}
              >
                <Icon
                  name="BookmarkIcon"
                  size={20}
                  variant={resource.bookmarked ? 'solid' : 'outline'}
                />
              </button>
            </div>
          )}
          <div className="absolute bottom-4 left-4">
            <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
              {resource.category}
            </span>
          </div>
        </div>

        <div className="p-6 flex flex-col h-full">
          <h3 className="text-xl font-bold text-text-primary mb-3 line-clamp-2">
            {resource.title}
          </h3>
          <p className="text-text-secondary mb-4 line-clamp-3">
            {resource.description}
          </p>
          <div className="flex items-center flex-wrap gap-2 text-xs text-text-secondary mb-3">
            <span className="font-semibold text-text-primary">{resource.author.name}</span>
            <span className="text-text-secondary">/ {resource.author.role}</span>
            <span aria-hidden="true">•</span>
            <span>{resource.publishedDate}</span>
            <span aria-hidden="true">•</span>
            <span>{resource.readTime} min read</span>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {resource.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-muted text-text-secondary text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
            <div className="flex flex-col">
              <p className="text-sm font-semibold text-text-primary">
                {resource.author.name}
              </p>
              <p className="text-xs text-text-secondary">
                {resource.author.role}
              </p>
            </div>
            <div className="flex items-center space-x-4 text-text-secondary text-sm">
              <div className="flex items-center space-x-1">
                <Icon name="ClockIcon" size={16} />
                <span>{resource.readTime} min</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="CalendarDaysIcon" size={16} />
                <span>{resource.publishedDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ResourceCard;
